import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PEOQuote } from '@/types/quotes.types';
import { useApplicationStatus } from './use-application-status';
import { useToast } from './use-toast';
import { useNavigate } from 'react-router-dom';

interface QuoteResponseInput {
  quoteId: string;
  status: 'interested' | 'accepted' | 'declined';
  notes?: string;
}

export function useQuotes() {
  const { data: application } = useApplicationStatus();
  const applicationId = application?.id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const fetchQuotes = async (): Promise<PEOQuote[]> => {
    if (!applicationId) {
      console.log('No application ID available');
      return [];
    }

    // This will only return quotes for the current user's application
    // thanks to Row Level Security in Supabase
    // Also filter to only return submitted quotes to the shopper
    const { data, error } = await supabase
      .from('peo_quotes')
      .select(`
        *,
        provider:provider_id (
          name,
          contact_email,
          website,
          years_in_business,
          rating
        ),
        application:application_id (
          id,
          company_legal_name,
          company_dba,
          primary_industry,
          first_name,
          last_name
        )
      `)
      .eq('application_id', applicationId)
      .eq('is_submitted', true); // Only show submitted quotes to users

    if (error) {
      console.error('Error fetching quotes:', error);
      throw new Error(error.message);
    }

    // Transform the data to include provider_name
    return data.map(quote => ({
      ...quote,
      provider_name: quote.provider?.name || 'Unknown Provider',
      provider_contact: quote.provider?.contact_email,
      provider_website: quote.provider?.website,
      provider_years: quote.provider?.years_in_business,
      provider_rating: quote.provider?.rating,
    }));
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['quotes', applicationId],
    queryFn: fetchQuotes,
    enabled: !!applicationId, // Only run query if we have an application ID
  });

  // Mutation for responding to quotes
  const respondToQuoteMutation = useMutation({
    mutationFn: async (input: QuoteResponseInput) => {
      setIsSubmitting(true);
      
      const { quoteId, status, notes } = input;
      const userId = (await supabase.auth.getUser()).data.user?.id;
      
      if (!userId) {
        throw new Error("User not authenticated");
      }
      
      // Add quote response
      const { data: responseData, error: responseError } = await supabase
        .from('quote_responses')
        .insert({
          quote_id: quoteId,
          status,
          response_notes: notes || null,
          user_id: userId
        })
        .select();
        
      if (responseError) {
        throw new Error(responseError.message);
      }
      
      // Update the quote status in peo_quotes table
      const { error: updateError } = await supabase
        .from('peo_quotes')
        .update({ status })
        .eq('id', quoteId);
        
      if (updateError) {
        throw new Error(updateError.message);
      }
      
      // If the quote was accepted, create a client record
      if (status === 'accepted' && applicationId) {
        // Get the user's profile id
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', userId)
          .single();
          
        if (profileError) {
          throw new Error(`Could not find user profile: ${profileError.message}`);
        }
        
        // Get the quote details to get the provider_id
        const { data: quoteData, error: quoteError } = await supabase
          .from('peo_quotes')
          .select('provider_id')
          .eq('id', quoteId)
          .single();
          
        if (quoteError) {
          throw new Error(`Could not find quote: ${quoteError.message}`);
        }
        
        // Create a client record
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .insert({
            id: userId,
            profile_id: profileData.id,
            application_id: applicationId,
            accepted_quote_id: quoteId,
            provider_id: quoteData.provider_id,
            onboarding_status: 'pending'
          })
          .select();
          
        if (clientError) {
          throw new Error(`Failed to create client record: ${clientError.message}`);
        }
      }
      
      // Send notification to the provider
      try {
        await supabase.functions.invoke('notify-quote-response', {
          body: { quoteId, status, notes }
        });
        console.log('Quote response notification sent successfully');
      } catch (notifyError) {
        console.error('Error sending notification:', notifyError);
        // Don't fail the response if notification fails
      }
      
      return responseData;
    },
    onSuccess: (_, variables) => {
      if (variables.status === 'accepted') {
        toast({
          title: "Quote Accepted",
          description: "Congratulations! Your quote has been accepted. We're preparing the next steps.",
        });
        // Redirect to the quote acceptance page
        navigate('/quote-accepted');
      } else if (variables.status === 'interested') {
        toast({
          title: "Interest Marked",
          description: "You've expressed interest in this quote. The provider will be notified.",
        });
      } else {
        toast({
          title: "Quote Declined",
          description: "Your response has been recorded.",
        });
      }
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['client-status'] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to respond to quote: ${error.message}`,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });

  const respondToQuote = (input: QuoteResponseInput) => {
    respondToQuoteMutation.mutate(input);
  };

  return {
    data,
    quotes: data || [],
    isLoading,
    error,
    respondToQuote,
    isSubmitting
  };
}
